import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Container,
  Typography,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  useScrollTrigger,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { NAV_ITEMS, navLinkProps, isNavActive } from '../data/navItems';

// One header for every page (home included): a petrol info strip that hides on
// scroll + a white blurred nav bar with dropdowns mirroring the original
// qasc.it menu. Mobile gets a full drawer with grouped items.

const TOPBAR_HEIGHT = 38;

const Topbar = styled(Box, { shouldForwardProp: (p) => p !== 'hidden' })(({ theme, hidden }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  zIndex: 1201,
  height: `${TOPBAR_HEIGHT}px`,
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#00425c',
  color: 'rgba(255,255,255,0.92)',
  fontSize: '13px',
  transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s',
  transform: hidden ? `translateY(-${TOPBAR_HEIGHT}px)` : 'translateY(0)',
  opacity: hidden ? 0 : 1,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const TopbarItem = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  '& svg': { fontSize: '15px', color: '#80acc0' },
});

const Bar = styled(AppBar, { shouldForwardProp: (p) => p !== 'scrolled' && p !== 'withTopbar' })(
  ({ theme, scrolled, withTopbar }) => ({
    backgroundColor: 'rgba(255,255,255,0.92)',
    backdropFilter: 'blur(14px)',
    color: '#00425c',
    boxShadow: scrolled ? '0 6px 24px rgba(0,66,92,0.10)' : 'none',
    borderBottom: scrolled ? '1px solid transparent' : '1px solid rgba(0,66,92,0.08)',
    borderRadius: 0,
    top: withTopbar ? `${TOPBAR_HEIGHT}px` : 0,
    transition: 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s',
    [theme.breakpoints.down('md')]: {
      top: 0,
    },
  })
);

const NavLink = styled(Typography, { shouldForwardProp: (p) => p !== 'active' })(
  ({ theme, active }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontFamily: '"Sora", sans-serif',
    fontSize: '14.5px',
    fontWeight: active ? 700 : 500,
    color: active ? '#6d8511' : '#00425c',
    textDecoration: 'none',
    padding: '10px 14px',
    borderRadius: '999px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      color: '#6d8511',
      backgroundColor: 'rgba(134,163,21,0.10)',
    },
    '& svg': { fontSize: '17px', marginTop: '1px' },
  })
);

const DropdownPanel = styled(Box)({
  position: 'absolute',
  top: '100%',
  left: '50%',
  transform: 'translateX(-50%)',
  paddingTop: '10px',
  zIndex: 1300,
});

const DropdownCard = styled(Box)({
  minWidth: '250px',
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  border: '1px solid rgba(0,66,92,0.08)',
  boxShadow: '0 18px 44px rgba(0,66,92,0.16)',
  padding: '10px',
  overflow: 'hidden',
});

const DropdownLink = styled(Typography)({
  display: 'block',
  fontSize: '14.5px',
  fontWeight: 500,
  color: '#00425c',
  textDecoration: 'none',
  padding: '10px 14px',
  borderRadius: '10px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(134,163,21,0.10)',
    color: '#6d8511',
    transform: 'translateX(3px)',
  },
});

const CtaButton = styled(Typography)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  fontFamily: '"Sora", sans-serif',
  backgroundColor: '#00425c',
  color: '#ffffff',
  fontSize: '14px',
  fontWeight: 600,
  textDecoration: 'none',
  padding: '10px 22px',
  borderRadius: '999px',
  whiteSpace: 'nowrap',
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: '#023144',
    transform: 'translateY(-1px)',
    boxShadow: '0 8px 20px rgba(0,66,92,0.30)',
  },
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}));

// Desktop item: link + optional hover dropdown.
function DesktopItem({ item, pathname }) {
  const [open, setOpen] = useState(false);
  const active = isNavActive(item, pathname);
  return (
    <Box
      sx={{ position: 'relative' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <NavLink {...navLinkProps(item)} active={active ? 1 : 0}>
        {item.label}
        {item.children && <ExpandMoreRoundedIcon />}
      </NavLink>
      {item.children && open && (
        <DropdownPanel>
          <DropdownCard>
            {item.children.map((c) => (
              <DropdownLink
                key={c.label}
                component={RouterLink}
                to={c.to}
                onClick={() => setOpen(false)}
              >
                {c.label}
              </DropdownLink>
            ))}
          </DropdownCard>
        </DropdownPanel>
      )}
    </Box>
  );
}

function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { pathname } = useLocation();
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 40 });

  return (
    <>
      {/* Info strip */}
      <Topbar hidden={scrolled ? 1 : 0}>
        <Container
          maxWidth="lg"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Box sx={{ display: 'flex', gap: '22px' }}>
            <TopbarItem>
              <EmailIcon />
              <Box
                component="a"
                href="mailto:info@qasc.it"
                sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#ffffff' } }}
              >
                info@qasc.it
              </Box>
            </TopbarItem>
            <TopbarItem>
              <PhoneIcon />
              <Box
                component="a"
                href="tel:+39041995202"
                sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#ffffff' } }}
              >
                +39 041 995202
              </Box>
            </TopbarItem>
          </Box>
          <TopbarItem>
            <AccessTimeIcon />
            Lun – Ven / 08:00 – 18:00
          </TopbarItem>
        </Container>
      </Topbar>

      {/* Main bar */}
      <Bar position="fixed" elevation={0} scrolled={scrolled ? 1 : 0} withTopbar={scrolled ? 0 : 1}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: { xs: '64px', md: '76px' },
            }}
          >
            <Box
              component={RouterLink}
              to="/"
              sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
              aria-label="QAS Consulting — Home"
            >
              <Box
                component="img"
                src="/assets/images/logo-dark.png"
                alt="QAS Consulting"
                sx={{ height: { xs: '44px', md: '54px' }, width: 'auto' }}
              />
            </Box>

            {!isMobile && (
              <Box component="nav" sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                {NAV_ITEMS.map((item) => (
                  <DesktopItem key={item.label} item={item} pathname={pathname} />
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <CtaButton component={RouterLink} to="/contatti">
                Contattaci
              </CtaButton>
              {isMobile && (
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  aria-label="Apri il menu"
                  sx={{ color: '#00425c' }}
                >
                  <MenuRoundedIcon sx={{ fontSize: '28px' }} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Container>
      </Bar>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 400 },
            backgroundColor: '#00425c',
            backgroundImage:
              'radial-gradient(circle at 85% 10%, rgba(134,163,21,0.18), transparent 42%), radial-gradient(circle at 10% 90%, rgba(128,172,192,0.16), transparent 45%)',
            padding: '26px',
            borderRadius: { xs: 0, sm: '24px 0 0 24px' },
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box
            component="img"
            src="/assets/images/logo.png"
            alt="QAS Consulting"
            sx={{ height: '48px' }}
          />
          <IconButton
            onClick={() => setDrawerOpen(false)}
            aria-label="Chiudi il menu"
            sx={{ color: '#ffffff' }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <List sx={{ mb: 2 }}>
          {NAV_ITEMS.map((item) => (
            <Box key={item.label}>
              <ListItemButton
                {...navLinkProps(item)}
                onClick={() => setDrawerOpen(false)}
                sx={{
                  borderBottom: '1px solid rgba(255,255,255,0.10)',
                  py: '12px',
                  px: '6px',
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    sx: {
                      color: isNavActive(item, pathname) ? '#8fc1d6' : '#ffffff',
                      fontFamily: '"Sora", sans-serif',
                      fontSize: '17px',
                      fontWeight: 600,
                    },
                  }}
                />
              </ListItemButton>
              {(item.children || []).map((c) => (
                <ListItemButton
                  key={c.label}
                  component={RouterLink}
                  to={c.to}
                  onClick={() => setDrawerOpen(false)}
                  sx={{ py: '8px', pl: '22px', pr: '6px' }}
                >
                  <ListItemText
                    primary={c.label}
                    primaryTypographyProps={{
                      sx: { color: 'rgba(255,255,255,0.72)', fontSize: '15px' },
                    }}
                  />
                </ListItemButton>
              ))}
            </Box>
          ))}
        </List>

        <Box sx={{ mt: 'auto' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px', mb: 1 }}>
            Viale Sanremo, 36 — 30038 Spinea (VE)
            <br />
            Tel. +39 041 995202
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px' }}>
            Via Monte Nero, 4 — 67043 Celano (AQ)
            <br />
            Tel. +39 0863 792752
          </Typography>
          <Typography
            component="a"
            href="mailto:info@qasc.it"
            sx={{
              display: 'inline-block',
              color: '#8fc1d6',
              fontSize: '14px',
              mt: 1.5,
              textDecoration: 'none',
            }}
          >
            info@qasc.it
          </Typography>
        </Box>
      </Drawer>
    </>
  );
}

export default Header;
